/**
 * R69c · Workspace-level Task Billing dashboard route
 * (/app/finance/task-billing).
 *
 * Distinct from the Subscription Billing page (/app/billing). This one
 * is `finance.view`-gated and rolls up billable-task hours/status.
 * Drill-down into a specific project opens the TaskBillingView filter.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import BillingDashboard from "@/components/billing/BillingDashboard";
import TaskBillingView from "@/components/tasks/TaskBillingView";
import { Receipt, ExternalLink, ArrowLeft } from "lucide-react";

export default function TaskBillingDashboardPage() {
  const { activeWorkspaceId } = useAuth();
  const navigate = useNavigate();
  // R69e · Three drill scopes: project, client, or assignee. Only one
  // active at a time; clicking Back returns to the workspace roll-up.
  const [drill, setDrill] = useState(null);  // {kind: 'project'|'client'|'assignee', id, label?}

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto space-y-4" data-testid="task-billing-page">
      <div className="flex items-center flex-wrap gap-3 mb-2">
        <div className="flex items-center gap-2">
          <Receipt className="w-5 h-5 text-primary" />
          <h1 className="text-lg font-bold uppercase tracking-widest">Task Billing</h1>
        </div>
        <span className="text-xs text-muted-foreground">
          Aggregated view of billable tasks across the workspace — WIP, AR, and revenue trends.
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/app/tasks")}
            className="text-[11px] uppercase tracking-widest px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1.5"
            data-testid="task-billing-open-tasks-view"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Tasks · Billing view
          </button>
        </div>
      </div>

      {!drill ? (
        <BillingDashboard
          wsId={activeWorkspaceId}
          onOpenProject={(pid) => setDrill({ kind: "project", id: pid })}
          onOpenClient={(cid) => setDrill({ kind: "client", id: cid })}
          onOpenAssignee={(uid, name) => setDrill({ kind: "assignee", id: uid, label: name })}
        />
      ) : (
        <div className="space-y-3" data-testid="task-billing-drill">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setDrill(null)}
              className="text-[11px] uppercase tracking-widest px-2 py-1 border border-border hover:bg-muted flex items-center gap-1"
              data-testid="task-billing-back"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to dashboard
            </button>
            <span className="text-xs text-muted-foreground">
              Drilling into {drill.kind}{drill.label ? ` · ${drill.label}` : ""}
            </span>
          </div>
          {/* Scoped mini-dashboard on top */}
          <BillingDashboard
            wsId={activeWorkspaceId}
            projectId={drill.kind === "project" ? drill.id : null}
            clientId={drill.kind === "client" ? drill.id : null}
            // No project-level drill inside a drill; assignee drill has
            // no scoped BillingDashboard param yet — TaskBillingView
            // below still filters correctly by assignee.
          />
          <div className="border-t border-border pt-4">
            <div className="label-eyebrow mb-3">
              Billable tasks
              {drill.kind === "assignee" && drill.label ? ` assigned to ${drill.label}` : ""}
            </div>
            <TaskBillingView
              wsId={activeWorkspaceId}
              projectId={drill.kind === "project" ? drill.id : null}
              clientId={drill.kind === "client" ? drill.id : null}
              assigneeId={drill.kind === "assignee" ? drill.id : null}
              assigneeName={drill.kind === "assignee" ? drill.label : null}
            />
          </div>
        </div>
      )}
    </div>
  );
}
